import { Button, CircularProgress, Stack, TextField } from '@mui/material';
import { Box } from '@mui/system';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useHistory, Link } from 'react-router-dom';
import { config } from '../App';
import Footer from './Footer';
import Header from './Header';
import './Login.css';

const Login = () => {
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setLoading] = useState(false);
    const [loginData, setLoginData] = useState({ username: '', password: '' });
    const history = useHistory();

    /**
     * Perform the Login API call
     * @param {{ username: string, password: string }} formData
     *  Object with values of username, password and confirm password user entered to register
     *
     * API endpoint - "POST /auth/login"
     *
     * Example for successful response from backend:
     * HTTP 201
     * {
     *      "success": true,
     *      "token": "testtoken",
     *      "username": "criodo",
     *      "balance": 5000
     * }
     *
     * Example for failed response from backend:
     * HTTP 400
     * {
     *      "success": false,
     *      "message": "Password is incorrect"
     * }
     *
     */
    const login = async (formData) => {
        if (!validateInput(formData)) return;
        try {
            setLoading(true);
            const API_URL = `${config.endpoint}/auth/login`;
            const res = await axios.post(API_URL, {
                username: formData.username,
                password: formData.password,
            });
            if (res.data.success) {
                persistLogin(
                    res.data.token,
                    res.data.username,
                    res.data.balance
                );
                enqueueSnackbar(
                    'Logged in with credentials of already registered user',
                    {
                        variant: 'success',
                        autoHideDuration: 2000,
                    }
                );
            }
            setLoading(false);
            history.push('/', { from: 'Login Component' });
        } catch (err) {
            setLoading(false);
            if (err.response && err.response.status === 400) {
                enqueueSnackbar(err.response.data.message, {
                    variant: 'error',
                    autoHideDuration: 2000,
                });
            } else {
                enqueueSnackbar(
                    'Something went wrong. Check that the backend is running, reachable and returns valid JSON.',
                    { variant: 'error', autoHideDuration: 2000 }
                );
            }
        }
    };

    // TODO: CRIO_TASK_MODULE_LOGIN - Validate the input
    /**
     * Validate the input values so that any bad or illegal values are not passed to the backend.
     *
     * @param {{ username: string, password: string }} data
     *  Object with values of username, password and confirm password user entered to register
     *
     * @returns {boolean}
     *    Whether validation has passed or not
     *
     * Return false and show warning message if any validation condition fails, otherwise return true.
     * (NOTE: The error messages to be shown for each of these cases, are given with them)
     * -    Check that username field is not an empty value - "Username is a required field"
     * -    Check that password field is not an empty value - "Password is a required field"
     */
    const validateInput = (data) => {
        const { username, password } = data;
        if (username.length === 0) {
            enqueueSnackbar('Username is a required field', {
                variant: 'warning',
                autoHideDuration: 2000,
            });
            return false;
        }
        if (username.length < 6) {
            enqueueSnackbar('Username must be at least 6 characters', {
                variant: 'warning',
                autoHideDuration: 2000,
            });
            return false;
        }
        if (password.length === 0) {
            enqueueSnackbar('Password is a required field', {
                variant: 'warning',
                autoHideDuration: 2000,
            });
            return false;
        }
        if (password.length < 6) {
            enqueueSnackbar('Password must be at least 6 characters', {
                variant: 'warning',
                autoHideDuration: 2000,
            });
            return false;
        }
        return true;
    };

    // TODO: CRIO_TASK_MODULE_LOGIN - Persist user's login information
    /**
     * Store the login information so that it can be used to identify the user in subsequent API calls
     *
     * @param {string} token
     *    API token used for authentication of requests after logging in
     * @param {string} username
     *    Username of the logged in user
     * @param {string} balance
     *    Wallet balance amount of the logged in user
     *
     * Make use of localStorage: https://developer.mozilla.org/en-US/docs/Web/API/Window/localStorage
     * -    `token` field in localStorage can be used to store the Oauth token
     * -    `username` field in localStorage can be used to store the username that the user is logged in as
     * -    `balance` field in localStorage can be used to store the balance amount in the user's wallet
     */
    const persistLogin = (token, username, balance) => {
        localStorage.setItem('token', token);
        localStorage.setItem('username', username);
        localStorage.setItem('balance', balance);
    };

    const loginInputHandler = (e) => {
        e.stopPropagation();
        setLoginData({ ...loginData, [e.target.name]: e.target.value });
    };

    return (
        <Box
            display="flex"
            flexDirection="column"
            justifyContent="space-between"
            minHeight="100vh"
        >
            <Header hasHiddenAuthButtons={false} />
            <Box className="content">
                <Stack spacing={2} className="form">
                    <h2 className="title">Login</h2>
                    <TextField
                        id="username"
                        label="Username"
                        variant="outlined"
                        title="Username"
                        name="username"
                        placeholder="Enter Username"
                        fullWidth
                        value={loginData.username}
                        onChange={loginInputHandler}
                    />
                    <TextField
                        id="password"
                        variant="outlined"
                        label="Password"
                        name="password"
                        type="password"
                        helperText="Password must be atleast 6 characters length"
                        fullWidth
                        placeholder="Enter a password with minimum 6 characters"
                        value={loginData.password}
                        onChange={loginInputHandler}
                    />
                    {isLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Button
                            className="button"
                            variant="contained"
                            onClick={(e) => {
                                e.stopPropagation();
                                login(loginData);
                            }}
                        >
                            Login To QKart
                        </Button>
                    )}
                    <p className="secondary-action">
                        Don't have an account?{' '}
                        <Link className="link" to="/register">
                            Register now
                        </Link>
                    </p>
                </Stack>
            </Box>
            <Footer />
        </Box>
    );
};

export default Login;
